import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import SubscriberCounter from './SubscriberCounter';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;

    // Reset states
    setSubmitStatus('idle');
    setErrorMessage('');
    
    // Validate email format
    const trimmedEmail = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(trimmedEmail)) {
      setErrorMessage('Please enter a valid email address.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);

    try {
      // First, check if email already exists
      const { data: existingSubscriber, error: checkError } = await supabase
        .from('newsletter_subscribers')
        .select('email')
        .eq('email', trimmedEmail)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is what we want
        // Any other error is a real problem
        throw new Error('Unable to verify subscription status. Please try again.');
      }

      if (existingSubscriber) {
        // Email already exists
        setErrorMessage('You\'re already subscribed! Check your inbox for our latest updates.');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Email doesn't exist, proceed with insertion
      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .insert([
          {
            email: trimmedEmail,
            status: 'active',
            source: 'website_footer'
          }
        ]);

      if (insertError) {
        // This shouldn't happen since we checked above, but just in case
        if (insertError.code === '23505') {
          setErrorMessage('You\'re already subscribed! Check your inbox for our latest updates.');
        } else {
          throw new Error('Unable to subscribe. Please try again later.');
        }
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Success! Now send welcome email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-welcome-email', {
          body: { email: trimmedEmail }
        });

        if (emailError) {
          // Email failed but subscription succeeded
          setSubmitStatus('success');
          setEmail('');
          // Don't show error to user, just log it for debugging
          console.warn('Welcome email failed to send:', emailError);
        } else {
          setSubmitStatus('success');
          setEmail('');
        }
      } catch (emailErr) {
        // Email failed but subscription succeeded
        setSubmitStatus('success');
        setEmail('');
        console.warn('Welcome email error:', emailErr);
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unable to subscribe. Please try again later.';
      setErrorMessage(errorMsg);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-teal-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Newsletter Section */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Stay Updated</h3>
          <p className="text-teal-100 mb-4">
            Get weekly tips, resources, and opportunities delivered to your inbox.
          </p>
          
          {/* Add Subscriber Counter */}
          <div className="mb-4">
            <SubscriberCounter />
          </div>

          <form onSubmit={handleNewsletterSubmit} data-readdy-form className="space-y-3">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              disabled={isSubmitting}
              className="flex-1 px-5 py-3 rounded-lg bg-white/95 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white text-base disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-8 py-3 bg-white text-teal-700 rounded-lg font-semibold hover:bg-emerald-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>

          {submitStatus === 'success' && (
            <div className="mt-4 p-3 bg-green-500/20 border border-green-400/30 rounded-lg">
              <p className="text-green-50 text-sm font-medium">
                🎉 Success! Check your email for a welcome message with exclusive resources.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mt-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg">
              <p className="text-red-50 text-sm font-medium">
                ⚠️ {errorMessage || 'Something went wrong. Please try again.'}
              </p>
            </div>
          )}
        </div>

        {/* Service Provider CTA Banner - Discrete Version */}
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 mb-12 border border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2 text-emerald-50">
                Interested in being listed on Ireland Bound?
              </h3>
              <p className="text-emerald-100 text-sm">
                Connect with those who wish to make Ireland their home.
              </p>
            </div>
            <a
              href="/dashboard"
              className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-all border border-white/20 hover:border-white/30 whitespace-nowrap"
            >
              Click to Apply
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <i className="ri-plane-line text-2xl text-teal-600"></i>
              </div>
              <span className="text-2xl font-bold">Ireland Bound</span>
            </div>
            <p className="text-emerald-100 mb-4">
              Your complete guide to relocating to Ireland with confidence and support.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all hover-lift"
              >
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all hover-lift"
              >
                <i className="ri-twitter-fill text-xl"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all hover-lift"
              >
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-white/20 transition-all hover-lift"
              >
                <i className="ri-instagram-fill text-xl"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <a href="/directory" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Service Directory
                </a>
              </li>
              <li>
                <a href="/citizenship" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Irish Citizenship
                </a>
              </li>
              <li>
                <a href="/community" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Community Hub
                </a>
              </li>
              <li>
                <a href="/tools" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Relocation Tools
                </a>
              </li>
              <li>
                <a href="/blog" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Blog & Guides
                </a>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-bold mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <a href="/tools/visa-quiz" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Visa Quiz
                </a>
              </li>
              <li>
                <a href="/tools/cost-calculator" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Cost Calculator
                </a>
              </li>
              <li>
                <a href="/tools/checklist" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Moving Checklist
                </a>
              </li>
              <li>
                <a href="/tools/salary-converter" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Salary Converter
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-bold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <a href="/privacy" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/terms" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/cookies" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Cookie Policy
                </a>
              </li>
              <li>
                <a href="/data-request" className="text-emerald-100 hover:text-white transition-colors hover:translate-x-1 inline-block whitespace-nowrap">
                  Data Request
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-emerald-100 text-sm">
            © 2024 Ireland Bound. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?origin=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-100 hover:text-white text-sm transition-colors whitespace-nowrap"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </footer>
  );
}